import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from '@clarix/ui';
import type { GeoOption } from './types';

interface GeoComboboxProps {
  id: string;
  /** Options for the CURRENT query (client-filtered upstream or server-searched). */
  options: GeoOption[];
  loading: boolean;
  /** Selected option id (the persisted value). */
  value: string | null;
  /** Display name for the selected id — survives when `options` doesn't contain it. */
  valueName: string | null;
  onChange: (option: GeoOption | null) => void;
  placeholder: string;
  disabled?: boolean;
  /**
   * When set, the search query is handled by the SERVER (cities): typing calls
   * this and the option list is replaced. Omitted → cmdk filters client-side.
   */
  onSearchChange?: (query: string) => void;
}

/**
 * Searchable single-select over the public geography catalog (countries /
 * states / cities). Popover + cmdk Command, matching the shadcn combobox
 * pattern. Selecting the active item again clears the selection.
 */
export function GeoCombobox({
  id,
  options,
  loading,
  value,
  valueName,
  onChange,
  placeholder,
  disabled,
  onSearchChange,
}: GeoComboboxProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="h-9 w-full justify-between font-normal"
        >
          <span className={cn('truncate', !valueName && 'text-muted-foreground')}>
            {valueName ?? placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={!onSearchChange}>
          <CommandInput
            placeholder="Search…"
            onValueChange={(q) => onSearchChange?.(q)}
          />
          <CommandList>
            <CommandEmpty>{loading ? 'Loading…' : 'No results.'}</CommandEmpty>
            {options.map((opt) => (
              <CommandItem
                key={opt.id}
                value={`${opt.name}__${opt.id}`}
                onSelect={() => {
                  onChange(opt.id === value ? null : opt);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn('mr-2 h-4 w-4', opt.id === value ? 'opacity-100' : 'opacity-0')}
                />
                {opt.name}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
